import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  EmailOutlined,
  ErrorOutlineOutlined,
  TrendingUp,
} from "@mui/icons-material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { AxiosService } from "../../services/axios/axiosService";
import CommonTablePagination from "../../components/CommonTablePagination/CommonTablePagination";
import { STATUS_CONFIG } from "../../constant";

const EMAIL_STATUS_CONFIG = {
  Sent: { label: "Sent", bg: "#E3F2FD", color: "#2563EB" },
  Delivered: { label: "Delivered", bg: "#d9f1ccd9", color: "#05af42" },
  Opened: { label: "Opened", bg: "#f3e8ff", color: "#7c3aed" },
  Pending: { label: "Pending", bg: "#FFF4E5", color: "#ED6C02" },
  Failed: STATUS_CONFIG.Failed,
};

const EmailStatusBadge = ({ status }) => {
  const config = EMAIL_STATUS_CONFIG[status] || {
    label: status || "—",
    bg: "#f3f4f6",
    color: "#4b5563",
  };

  return (
    <Chip
      label={config.label}
      size="small"
      sx={{
        backgroundColor: config.bg,
        color: config.color,
        fontWeight: 500,
        fontSize: "0.72rem",
        height: 24,
        px: 1,
        border: `1px solid ${config.color}30`,
      }}
    />
  );
};

const formatTableDate = (date) =>
  date ? format(new Date(date), "MM/dd/yyyy") : null;

const parseDashboardRows = (res) => {
  if (Array.isArray(res)) return res;
  if (res && typeof res === "object") {
    if (Array.isArray(res.data)) return res.data;
    if (Array.isArray(res.items)) return res.items;
    if (Array.isArray(res.emails)) return res.emails;
  }
  return [];
};

const mapEmailRow = (item) => {
  const emailTemplateName =
    item.emailTemplateName?.trim() ||
    item.EmailTemplateName?.trim() ||
    "";
  return {
    id: item.id,
    candidateName: item.candidateName?.trim() || "—",
    recipient: item.recipient || "—",
    positionTitle: item.positionTitle || "—",
    emailTemplateName: emailTemplateName || "—",
    sentAt: item.sentAt,
    status: item.status || "—",
    interviewId: item.interviewId ?? null,
    errorMessage: item.errorMessage ?? null,
  };
};

const buildSummary = (rawList, rows) => {
  const first = rawList[0];
  const totalEmails =
    typeof first?.totalEmails === "number"
      ? first.totalEmails
      : rows.length;
  const totalSent =
    typeof first?.totalSent === "number"
      ? first.totalSent
      : rows.filter((r) => r.status === "Sent" || r.status === "Delivered").length;
  const totalFailed =
    typeof first?.totalFailed === "number"
      ? first.totalFailed
      : rows.filter((r) => r.status === "Failed").length;
  const pending = Math.max(0, totalEmails - totalSent - totalFailed);
  const opened = rows.filter((r) => r.status === "Opened").length;

  const deliveryRatePct =
    totalEmails > 0 ? Math.round((totalSent / totalEmails) * 100) : 0;
  const failureRatePct =
    totalEmails > 0 ? Math.round((totalFailed / totalEmails) * 100) : 0;
  const pendingRatePct =
    totalEmails > 0 ? Math.round((pending / totalEmails) * 100) : 0;
  const openRatePct =
    totalEmails > 0 ? Math.round((opened / totalEmails) * 100) : 0;

  return {
    totalEmails,
    totalSent,
    totalFailed,
    pending,
    opened,
    deliveryRatePct,
    failureRatePct,
    pendingRatePct,
    openRatePct,
  };
};

const TABLE_COLUMNS = [
  { label: "Candidate Name" },
  { label: "Email" },
  { label: "Position" },
  { label: "Email Type" },
  { label: "Sent At" },
  { label: "Status" },
];

const EmailActivity = () => {
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(() => buildSummary([], []));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [emailTemplateFilter, setEmailTemplateFilter] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const loadEmailDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
        const response = await AxiosService.get(
            `/api/Interview/email-dashboard`,
          );
      //const res = await AxiosService.get("/api/Interview/email-dashboard");
      const rawList = parseDashboardRows(response);
      const mapped = rawList.map(mapEmailRow);
      setRows(mapped);
      setSummary(buildSummary(rawList, mapped));
    } catch (err) {
      setRows([]);
      setSummary(buildSummary([], []));
      setError(
        err?.message || "Unable to load email activity. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEmailDashboard();
  }, [loadEmailDashboard]);

  const statusFilterOptions = useMemo(() => {
    const statuses = new Set(rows.map((r) => r.status).filter((s) => s && s !== "—"));
    return [
      { value: "", label: "All" },
      ...Array.from(statuses).sort().map((s) => ({
        value: s,
        label: EMAIL_STATUS_CONFIG[s]?.label ?? s,
      })),
    ];
  }, [rows]);

  const emailTemplateFilterOptions = useMemo(() => {
    const templates = new Set(
      rows.map((r) => r.emailTemplateName).filter((t) => t && t !== "—"),
    );
    return [
      { value: "", label: "All" },
      ...Array.from(templates).sort((a, b) => a.localeCompare(b)).map((t) => ({
        value: t,
        label: t,
      })),
    ];
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesStatus = statusFilter ? row.status === statusFilter : true;
      const matchesTemplate = emailTemplateFilter
        ? row.emailTemplateName === emailTemplateFilter
        : true;
      if (!matchesStatus || !matchesTemplate) return false;
      if (!q) return true;
      return (
        row.candidateName.toLowerCase().includes(q) ||
        row.recipient.toLowerCase().includes(q) ||
        row.emailTemplateName.toLowerCase().includes(q) ||
        row.positionTitle.toLowerCase().includes(q)
      );
    });
  }, [rows, statusFilter, emailTemplateFilter, search]);

  const maxPage = useMemo(() => {
    if (filteredRows.length === 0) return 0;
    return Math.max(0, Math.ceil(filteredRows.length / rowsPerPage) - 1);
  }, [filteredRows.length, rowsPerPage]);

  const safePage = Math.min(page, maxPage);

  useEffect(() => {
    if (loading) return;
    if (page > maxPage) setPage(maxPage);
  }, [page, maxPage, loading]);

  const paginatedRows = useMemo(
    () =>
      filteredRows.slice(
        safePage * rowsPerPage,
        safePage * rowsPerPage + rowsPerPage,
      ),
    [filteredRows, safePage, rowsPerPage],
  );

  const summaryCards = useMemo(
    () => [
      {
        id: "total",
        title: "Total Emails",
        value: summary.totalEmails.toLocaleString(),
        hint: "All tracked emails",
        icon: (
          <EmailOutlined sx={{ fontSize: 22, color: theme.palette.primary.main }} />
        ),
        iconBg: alpha(theme.palette.primary.main, 0.12),
      },
      {
        id: "sent",
        title: "Sent",
        value: summary.totalSent.toLocaleString(),
        hint: `${summary.deliveryRatePct}% sent successfully`,
        icon: (
          <TrendingUp sx={{ fontSize: 22, color: theme.palette.success.main }} />
        ),
        iconBg: alpha(theme.palette.success.main, 0.12),
      },
      {
        id: "failed",
        title: "Failed",
        value: summary.totalFailed.toLocaleString(),
        hint: `${summary.failureRatePct}% failure rate`,
        icon: (
          <ErrorOutlineOutlined sx={{ fontSize: 22, color: theme.palette.error.main }} />
        ),
        iconBg: alpha(theme.palette.error.main, 0.12),
      },
    //   {
    //     id: "pending",
    //     title: "Pending",
    //     value: summary.pending.toLocaleString(),
    //     hint: `${summary.pendingRatePct}% in queue`,
    //     icon: <EmailOutlined color="warning" />,
    //   },
    //   {
    //     id: "opened",
    //     title: "Opened",
    //     value: summary.opened.toLocaleString(),
    //     hint: `${summary.openRatePct}% open rate`,
    //     icon: <RemoveRedEye color="secondary" />,
    //   },
    ],
    [summary, theme],
  );

 

  const renderLoading = () => (
    <TableRow>
      <TableCell colSpan={TABLE_COLUMNS.length}>
        <Box
          sx={{
            py: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <CircularProgress size={40} thickness={4} sx={{ color: "primary.main" }} />
          <Typography sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
            Loading email activity…
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  const renderEmpty = (text) => (
    <TableRow>
      <TableCell colSpan={TABLE_COLUMNS.length}>
        <Typography
          sx={{
            fontWeight: 500,
            color: theme.palette.text.secondary,
            fontSize: "0.9375rem",
          }}
        >
          {text}
        </Typography>
      </TableCell>
    </TableRow>
  );

  return (
    <Stack spacing={3}>
      {error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={loadEmailDashboard}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(3, 1fr)",
          },
        }}
      >
        {summaryCards.map((c) => (
          <Card
            key={c.id}
            variant="outlined"
            sx={{ borderRadius: 3, boxShadow: theme.shadows[1] }}
          >
            <CardContent sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {c.title}
                </Typography>
                {loading ? (
                  <Skeleton width={80} height={36} />
                ) : (
                  <Typography variant="h5" fontWeight={700}>
                    {c.value}
                  </Typography>
                )}
                {/* <Typography variant="caption" color="text.secondary">
                  {c.hint}
                </Typography> */}
              </Box>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: c.iconBg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {c.icon}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 3,
          overflow: "hidden",
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Email Logs
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <TextField
              size="small"
              placeholder="Search by name, email.."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon
                      sx={{ fontSize: 18, color: theme.palette.text.secondary }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: 240,
                "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
              }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }} disabled={loading}>
              <InputLabel id="email-template-filter-label" shrink>
                Email Type
              </InputLabel>
              <Select
                labelId="email-template-filter-label"
                id="email-template-filter"
                value={emailTemplateFilter}
                label="Email Type"
                size="small"
                onChange={(e) => {
                  setEmailTemplateFilter(e.target.value);
                  setPage(0);
                }}
                displayEmpty
                renderValue={(v) =>
                  emailTemplateFilterOptions.find((o) => o.value === v)?.label ?? "All"
                }
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: 1.5,
                  fontSize: "0.875rem",
                }}
              >
                {emailTemplateFilterOptions.map((opt) => (
                  <MenuItem
                    key={opt.value || "all-template"}
                    value={opt.value}
                    sx={{ fontSize: "0.875rem" }}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {TABLE_COLUMNS.map((col) => (
                  <TableCell
                    key={col.label}
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      fontSize: "0.875rem",
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? renderLoading()
                : filteredRows.length === 0
                  ? renderEmpty(
                      error
                        ? "Unable to load emails."
                        : "No emails match the selected filters.",
                    )
                  : paginatedRows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell sx={{ fontWeight: 500, textTransform: "capitalize" }}>
                          {row.candidateName || "-"}
                        </TableCell>
                        <TableCell>{row.recipient || "-"}</TableCell>
                        <TableCell>{row.positionTitle || "-"}</TableCell>
                        <TableCell sx={{ textWrap: "wrap" }} title={row.emailTemplateName}>
                          {row.emailTemplateName || "-"}
                        </TableCell>
                        <TableCell>
                          {formatTableDate(row.sentAt) || (
                            <Box component="span" sx={{ color: theme.palette.text.disabled }}>
                              —
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <EmailStatusBadge status={row.status} />
                        </TableCell>
                      </TableRow>
                    ))}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredRows.length > 0 && !loading && (
          <CommonTablePagination
            count={filteredRows.length}
            page={safePage}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
          />
        )}
      </Paper>
    </Stack>
  );
};

export default EmailActivity;
